import type { Except, Optional } from '@festina-life/core'
import { useCallback, useMemo } from 'react'
import {
  NumericFormat,
  type NumericFormatProps,
  numericFormatter,
  type OnValueChange,
} from 'react-number-format'

import { RawTextInput, type RawTextInputProps } from '../textInput/RawTextInput'
import { useNumberFormatOptions } from './useNumberFormatOptions'

export type RawNumberInputProps = Except<
  RawTextInputProps,
  'value' | 'onChange' | 'type' | 'defaultValue' | 'placeholder'
> & {
  value?: Optional<number>
  name?: string
  defaultValue?: Optional<string | number>
  placeholder?: Optional<string | number>
  onChange?: (value: number | undefined) => void
} & Pick<NumericFormatProps, 'decimalScale' | 'thousandSeparator'>

export const RawNumberInput = ({
  value,
  onChange,
  name,
  placeholder,
  decimalScale,
  ...restProps
}: RawNumberInputProps) => {
  const numberFormatOptions = useNumberFormatOptions(decimalScale)

  const formattedPlaceholder = useMemo(
    () => numericFormatter(placeholder?.toString() ?? '', numberFormatOptions),
    [numberFormatOptions, placeholder],
  )

  const handleChange: OnValueChange = useCallback(
    (values) => {
      onChange?.(values.floatValue)
    },
    [onChange],
  )

  return (
    <NumericFormat
      value={value ?? ''}
      onValueChange={handleChange}
      name={name}
      size={(value?.toString().length ?? 0) + 1} // Without this inputs have size 20 as default dictating a too wide min width — rawTextInputStyles sets w-full so it fills container.
      customInput={RawTextInput}
      placeholder={formattedPlaceholder}
      {...numberFormatOptions}
      {...restProps}
    />
  )
}
